/**
 * Service worker update check on resume (byte-identical in owner + reception).
 *
 * Browsers only re-check the worker script on navigation, so a PWA left in the background for days
 * keeps running the old worker. On resume we ask for `registration.update()`; a changed worker then
 * goes through its own lifecycle (owner: SKIP_WAITING + idle update toast, reception staff-sw:
 * skipWaiting on install). Never reloads the page here — a reload on resume could discard an
 * in-progress checkout or form.
 */
import { onResume } from "./resumeCoordinator";

export const SW_UPDATE_MIN_INTERVAL_MS = 60_000;

let lastUpdateAt = 0;

/** @returns {Promise<boolean>} true when an update check was issued */
export async function requestServiceWorkerUpdate() {
  if (typeof navigator === "undefined" || typeof navigator.serviceWorker?.getRegistration !== "function") {
    return false;
  }
  const now = Date.now();
  if (now - lastUpdateAt < SW_UPDATE_MIN_INTERVAL_MS) return false;
  lastUpdateAt = now;
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration || typeof registration.update !== "function") return false;
    await registration.update();
    return true;
  } catch {
    return false;
  }
}

/** @returns {() => void} unsubscribe */
export function initServiceWorkerUpdateOnResume() {
  return onResume(() => {
    void requestServiceWorkerUpdate();
  });
}

/** Test-only. */
export function __resetServiceWorkerResumeUpdateForTests() {
  lastUpdateAt = 0;
}
